import { lastValueFrom } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';

import { PORTAL_API_BASE_URL } from '@echospaces-web-monorepo/es-portal-shell';

import type { SpaceRole } from './space-members.service';

export interface MyGrant {
  spaceId: string;
  role: SpaceRole;
}

export type ParentLookup = (spaceId: string) => string | null;

/**
 * Tracks the calling user's per-space edit-tier grants (EDITOR + ADMIN).
 * The portal SPA loads this once per session and uses `canEditAt` /
 * `canManageAt` to drive UI gating: the tree marks nodes editable, the
 * detail panel reveals member-management actions, etc. Closure walk
 * happens client-side via a parent-lookup the caller provides — that
 * lookup is built from the already-loaded tree, so no extra fetches.
 */
@Injectable({ providedIn: 'root' })
export class MyGrantsService {
  private readonly http = inject(HttpClient);
  private readonly api = inject(PORTAL_API_BASE_URL);

  private readonly _grants = signal<MyGrant[]>([]);
  private readonly _loaded = signal(false);

  readonly grants = this._grants.asReadonly();
  readonly loaded = this._loaded.asReadonly();

  async loadGrants(): Promise<void> {
    const data = await lastValueFrom(this.http.get<MyGrant[]>(`${this.api}/ecms/me/grants`));
    this._grants.set(data);
    this._loaded.set(true);
  }

  /**
   * True if the user has ADMIN coverage on `spaceId` or any ancestor.
   * Walks up via `parentOf` until a grant hits or the root is reached.
   */
  canManageAt(spaceId: string, parentOf: ParentLookup): boolean {
    return this.walkUpHasRole(spaceId, parentOf, (role) => role === 'admin');
  }

  /**
   * True if the user has EDITOR-or-ADMIN coverage on `spaceId` or any ancestor.
   */
  canEditAt(spaceId: string, parentOf: ParentLookup): boolean {
    return this.walkUpHasRole(spaceId, parentOf, (role) => role === 'editor' || role === 'admin');
  }

  private walkUpHasRole(
    spaceId: string,
    parentOf: ParentLookup,
    predicate: (role: SpaceRole) => boolean,
  ): boolean {
    const grantsByspace = new Map(this._grants().map((g) => [g.spaceId, g.role]));
    let current: string | null = spaceId;
    const seen = new Set<string>();
    while (current && !seen.has(current)) {
      seen.add(current);
      const role = grantsByspace.get(current);
      if (role && predicate(role)) return true;
      current = parentOf(current);
    }
    return false;
  }
}
